import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Alert,
    CircularProgress
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { SapService } from '../services/api';
import PoliticasMatricula from '../components/common/PoliticasMatricula';

// Tipografía propia de la pantalla de login (IBM Plex: técnica y muy legible en textos densos)
const PLEX = '"IBM Plex Sans", "Inter", "Helvetica", "Arial", sans-serif';
const conPlex = (base) => {
    const typography = { fontFamily: PLEX };
    Object.entries(base.typography).forEach(([k, v]) => {
        if (v && typeof v === 'object' && 'fontFamily' in v) typography[k] = { ...v, fontFamily: PLEX };
    });
    return createTheme({ ...base, typography: { ...base.typography, ...typography } });
};

export default function Login() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleGoogleSuccess = async (credentialResponse) => {
        setLoading(true);
        setError('');
        try {
            const result = await SapService.loginGoogleApp(credentialResponse.credential);
            login(result.token, result.user);
            navigate('/');
        } catch (err) {
            setError(err.message || 'Error al iniciar sesión con Google.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <ThemeProvider theme={conPlex}>
        <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column-reverse', md: 'row' },
            minHeight: '100vh',
            height: { md: '100vh' },
            overflow: { md: 'hidden' }
        }}>
            {/* Panel izquierdo - marca */}
            <Box
                sx={{
                    display: 'flex',
                    width: { xs: '100%', md: '55%' },
                    background: 'linear-gradient(150deg, #1a1a2e 0%, #16213e 55%, #0f3460 100%)',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                <Box sx={{
                    position: 'absolute', top: -100, right: -100,
                    width: 350, height: 350, borderRadius: '50%',
                    background: 'rgba(211, 47, 47, 0.12)'
                }} />
                <Box sx={{
                    position: 'absolute', bottom: -120, left: -60,
                    width: 450, height: 450, borderRadius: '50%',
                    background: 'rgba(211, 47, 47, 0.08)'
                }} />
                <Box sx={{
                    position: 'absolute', top: '40%', right: -40,
                    width: 180, height: 180, borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.03)'
                }} />

                <Box sx={{
                    position: 'relative', zIndex: 1, flex: 1,
                    overflowY: { md: 'auto' },
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    px: { xs: 3, sm: 3, lg: 4 }, pt: 1.5, pb: 1.5
                }}>

                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    <PoliticasMatricula />
                </Box>
                </Box>
            </Box>

            {/* Panel derecho - login */}
            <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                overflowY: { md: 'auto' },
                bgcolor: '#f8fafc',
                px: 3,
                py: { xs: 4, md: 3 }
            }}>
                {/* Encabezado - marca */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                <Box
                    component="img"
                    src={`${process.env.PUBLIC_URL}/assets/Talsa-Logo.jpg`}
                    alt="Talsa"
                    sx={{ height: 76, mb: 1 }}
                />
                <Typography variant="h3" sx={{
                    color: '#0f172a', fontWeight: 700, textAlign: 'center',
                    fontSize: { xs: '1.75rem', md: '2.1rem' },
                    whiteSpace: { md: 'nowrap' },
                    mb: 0.5, lineHeight: 1.2
                }}>
                    Creación de Materiales
                </Typography>
                <Typography variant="body1" sx={{
                    color: 'text.secondary', textAlign: 'center',
                    maxWidth: 440, lineHeight: 1.5, fontSize: '0.92rem'
                }}>
                    Sistema de automatización para la gestión y creación de materiales en SAP Business ByDesign.
                </Typography>

                <Box sx={{ mt: 1.5, display: 'flex', gap: 3 }}>
                    {[
                        { num: '01', label: 'Configurar' },
                        { num: '02', label: 'Detallar' },
                        { num: '03', label: 'Confirmar' },
                    ].map(step => (
                        <Box key={step.num} sx={{ textAlign: 'center' }}>
                            <Typography sx={{ color: '#D32F2F', fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>
                                {step.num}
                            </Typography>
                            <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem', lineHeight: 1.3 }}>
                                {step.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
                </Box>

                <Paper elevation={0} sx={{
                    p: { xs: 3, sm: 5 },
                    width: '100%',
                    maxWidth: 440,
                    borderRadius: 3,
                    border: '1px solid rgba(0,0,0,0.08)',
                    bgcolor: '#ffffff'
                }}>

                    <Typography variant="h4" fontWeight={700} sx={{ color: '#0f172a', mb: 0.5 }}>
                        Bienvenido
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                        Ingresa con tu cuenta corporativa <strong>@talsa.com.co</strong> para acceder al sistema.
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                        {loading ? (
                            <CircularProgress size={32} sx={{ color: '#D32F2F' }} />
                        ) : (
                            <GoogleLogin
                                onSuccess={handleGoogleSuccess}
                                onError={() => setError('Error al iniciar sesión con Google.')}
                                size="large"
                                width="360"
                                text="signin_with"
                                shape="rectangular"
                                locale="es"
                            />
                        )}
                    </Box>

                    <Typography variant="caption" color="text.disabled" sx={{ mt: 5, display: 'block', textAlign: 'center' }}>
                        © {new Date().getFullYear()} TALSA S.A. — Acceso restringido a usuarios autorizados.
                    </Typography>
                </Paper>
            </Box>
        </Box>
        </ThemeProvider>
    );
}
